import { PrivateRoutes } from '@/constants/routes'
import useTableDataDinamic from '@/hooks/useTableDataDinamic'
import { IConfigSeller } from '@/models/configSeller'
import { IUser } from '@/models/user.model'
import { webApiService } from '@/services'
import { currencyFormat } from '@/utils/currencyFormat'
import { StatusColor } from '@/utils/StatusColor.utility'
import { Dropdown, DropdownTrigger, DropdownMenu, DropdownItem, Button, Chip, Input, Link, Select, SelectItem, Spinner, Table, TableBody, TableCell, TableColumn, TableHeader, TableRow } from '@nextui-org/react'
import { motion } from 'framer-motion'
import { CSSProperties, FC, useEffect, useState } from 'react'
import { MdOutlineSearch, MdCloudDownload, MdDone, MdCancel } from 'react-icons/md'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import CancelTradein from '../../TradeInPage/components/CancelTradein'
import ReceiveDevice from '../../TradeInPage/components/ReceiveDevice'
import UploadTicket from '../../TradeInPage/components/UploadTicket'
import { TradeinProvider } from '../../TradeInPage/context/TradeinContext'
import CustomDateRangePicker from '../components/CustomDateRangePicker/CustomDateRangePicker'
import ModalTestPhonecheck from '../components/TablePrincipal/ModalTestPhonecheck'

const columns = [
  { name: '#', uid: 'id' },
  { name: 'Nombre', uid: 'name' },
  { name: 'Email', uid: 'email' },
  { name: 'Dispositivo', uid: 'sbu' },
  { name: 'Marca', uid: 'brand' },
  { name: 'Serie/imei', uid: 'serie' },
  { name: 'Modelo', uid: 'model' },
  { name: 'Monto Descuento', uid: 'discount_amount' },
  { name: 'Aporte', uid: 'contribution_amount' },
  { name: 'Código', uid: 'login_code' },
  { name: 'Estado', uid: 'status' },
  { name: 'Motivo', uid: 'cancel_reason' },
  { name: 'Seller', uid: 'seller' },
  { name: '', uid: 'actions' }
]

const excelName = 'Listado_TradeIn_Principal'

/* Recibir y rechazar el equipo desde el listado: las condiciones se evalúan con los campos que ya
   trae el listado (id_status, hasfiles, extra_controls_quoted), y el modal del detalle se monta solo
   al hacer click, así la carga de la página no dispara una petición por fila. */
const AccionesEquipo: FC<{ item: any, onDone: () => void }> = ({ item, onDone }) => {
  const [modal, setModal] = useState<'' | 'receive' | 'cancel'>('')
  const userState = useSelector((store: { user: IUser }) => store.user)
  const isSoloLectura = userState.id_role === Number(import.meta.env.VITE_PERFIL_SOLOLECTURA)
  const isCotizador = userState.id_role === Number(import.meta.env.VITE_PERFIL_TI_COTIZADOR)
  const idStatus = Number(item.id_status)

  // Si se cotizaron controles extra se exigen las fotos (hasfiles); si no se cotizaron (null o 0, que
  // es lo que llega de los sellers que ya no preguntan por controles), basta el estado.
  const hasFiles = Number(item.hasfiles ?? 0)
  const extraControlsQuoted = Number(item.extra_controls_quoted ?? 0)
  const canReceive = idStatus === 3 && (hasFiles === 1 || extraControlsQuoted === 0) && !isSoloLectura && !isCotizador
  // No se rechaza un tradein entregado (5), retirado (7), rechazado (8) ni en custodia (9).
  const canCancel = ![5, 7, 8, 9].includes(idStatus) && !isSoloLectura
  // El ticket de venta recién existe cuando el equipo ya fue entregado (5).
  const canUploadTicket = idStatus === 5 && !isSoloLectura && !isCotizador
  // Si el listado ya trae el ticket cargado, el botón abre el visor en vez de la cámara.
  const ticketUrl = String(item.ticket_url ?? '')

  const handleDone = () => {
    setModal('')
    onDone()
  }

  if (!canReceive && !canCancel && !canUploadTicket) return <span className='text-xs'>-</span>

  return (
    <div className='flex items-center justify-center'>
      {canReceive && (
        <Button color='primary' variant='shadow' size='sm' endContent={<MdDone style={{ fontSize: '15px' }} />} onPress={() => setModal('receive')}>
          Recibir equipo
        </Button>
      )}
      {canCancel && (
        <Button color='danger' variant='shadow' size='sm' className='ml-2' endContent={<MdCancel style={{ fontSize: '12px' }} />} onPress={() => setModal('cancel')}>
          Rechazar Tradein
        </Button>
      )}
      {canUploadTicket && (
        <UploadTicket idTradein={item.id} ticketUrl={ticketUrl} onDone={onDone} />
      )}
      {modal === 'receive' && (
        <TradeinProvider idProps={item.id} light>
          <ReceiveDevice autoOpen onClosed={() => setModal('')} onDone={handleDone} />
        </TradeinProvider>
      )}
      {modal === 'cancel' && (
        <CancelTradein autoOpen idTradein={item.id} onClosed={() => setModal('')} onDone={handleDone} />
      )}
    </div>
  )
}

const TablePrincipalLegacy = () => {
  const [showCustomDateModal, setShowCustomDateModal] = useState(false)
  const [customStart, setCustomStart] = useState('')
  const [customEnd, setCustomEnd] = useState('')
  const [regions, setRegions] = useState<any[]>([])
  const [stores, setStores] = useState<any[]>([])
  const [selectedRegion, setSelectedRegion] = useState('')
  const [selectedStore, setSelectedStore] = useState('')
  const configSeller = useSelector((store: { config: IConfigSeller }) => store.config)
  const isTelcelCorpMexico = configSeller.config.s3.FOLDER_APP === 'ti_telcel_corp_mx'
  const {
    sortDescriptor,
    filterValue,
    sortedItems,
    bottomContent,
    onSearchChange,
    setSortDescriptor,
    loading,
    onClear,
    loadingExcel,
    exportToCSV,
    reload
  } = useTableDataDinamic(columns.concat([{ name: 'Tienda', uid: 'store' }]), excelName, 'all', [1, 2, 3, 4, 5, 6, 7, 8, 9, 11], selectedRegion, selectedStore)
  const isHuaweiMexico = configSeller.config.s3.FOLDER_APP === 'ti_huawei_mx'
  const isWomChile = configSeller.config.s3.FOLDER_APP === 'ti_wom'
  const isHuaweiOnlineChile = configSeller.config.s3.FOLDER_APP === 'ti_huawei_online_cl'
  const isHuaweiOnlineMexico = configSeller.config.s3.FOLDER_APP === 'ti_huawei_online_mx'
  const isHuaweiOnlinePeru = configSeller.config.s3.FOLDER_APP === 'ti_huawei_online_pe'
  const isReuseChile = configSeller.config.s3.FOLDER_APP === 'ti_reusecl'
  const isConectaPeru = configSeller.config.s3.FOLDER_APP === 'ti_conecta_peru'
  const isRipleyPeru = configSeller.config.s3.FOLDER_APP === 'ti_ripley_pe'
  const isHuaweiPeru = configSeller.config.s3.FOLDER_APP === 'ti_huawei_pe'
  const isCoppelMexico = configSeller.config.s3.FOLDER_APP === 'ti_coppel'
  const isLiverpoolMexico = configSeller.config.s3.FOLDER_APP === 'ti_liverpool_mx'
  const userState = useSelector((store: { user: IUser }) => store.user)
  const isTiRegion = userState.id_role === Number(import.meta.env.VITE_PERFIL_TI_REGION)
  const isAdmin = userState.id_role === Number(import.meta.env.VITE_PERFIL_ADMIN)
  const { slug } = useParams()

  const loadRegions = async () => {
    await webApiService.getStoreRegions(slug).then((response) => {
      setRegions(response.data ?? response ?? [])
    })
  }

  const handleRegionChange = async (region: string) => {
    setSelectedRegion(region)
    setSelectedStore('')
    setStores([])
    if (!region) return
    await webApiService.getStoresByRegion(slug, region).then((response) => {
      setStores(response.data ?? response ?? [])
    })
  }

  const loadUserRegion = async () => {
    await webApiService.getRegionByUser(userState.id).then((response) => {
      const raw = response.data ?? response
      const reg = Array.isArray(raw) ? raw[0] : raw
      const value = reg == null ? '' : String(reg.region ?? reg.name ?? reg)
      if (value) handleRegionChange(value)
    })
  }

  useEffect(() => {
    if (!isCoppelMexico) return
    loadRegions()
    if (isTiRegion) loadUserRegion()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const renderCell: any = (item: any, columnKey: any) => {
    const cellValue = item[columnKey]

    /* eslint default-case: "error" */
    switch (columnKey) {
      case 'discount_amount':
        return (<span className='font-bold text-xs'>{currencyFormat(Number(cellValue), import.meta.env.VITE_COUNTRY)}</span>)
      case 'contribution_amount':
        return cellValue === null || cellValue === undefined ? '' : (<span className='font-bold text-xs'>{currencyFormat(Number(cellValue), import.meta.env.VITE_COUNTRY)}</span>)
      case 'name':
        return (<span className='font-bold text-xs'>{item[columnKey] + ' ' + item.lastname}</span>)
      case 'status':
        return (<Chip color='primary' style={{ '--status-color': `${StatusColor(cellValue)}`, backgroundColor: 'var(--status-color)' } as CSSProperties} size='sm' variant='solid' className=''>{cellValue}</Chip>)
      case 'actions':
        return (<Button size='sm' as={Link} href={PrivateRoutes.TRADEIN.replace(':slug', String(slug)).replace(':id', item.id)} className='bg-black text-white'>Revisar</Button>)
      case 'login_code':
        return (<span className='text-xs'>{item.login_code || item.login_code_novabench || '-'}</span>)
      case 'phonecheck_test':
        return item.login_code
          ? (<ModalTestPhonecheck item={item} onDone={reload} />)
          : (<span className='text-xs'>-</span>)
      case 'receive_actions':
        return (<AccionesEquipo item={item} onDone={reload} />)
      default:
        return (<span className='text-xs'>{cellValue}</span>)
    }
  }

  // La columna de prueba solo se agrega al render, no al listado que arma el excel.
  const visibleColumns = (configSeller.config.permisos.ENABLE_CONTRIBUTION !== 1)
    ? columns.filter(e => e.uid !== 'serie' && e.uid !== 'cancel_reason' && e.uid !== 'contribution_amount')
    : columns.filter(e => e.uid !== 'serie' && e.uid !== 'cancel_reason')
  const columnsWithTest = isTelcelCorpMexico
    ? visibleColumns.flatMap(col => col.uid === 'login_code' ? [col, { name: 'Prueba', uid: 'phonecheck_test' }] : [col])
    : visibleColumns
  // Liverpool recibe y rechaza el equipo desde el listado, al lado de la columna Seller.
  const headerColumns = isLiverpoolMexico
    ? columnsWithTest.flatMap(col => col.uid === 'seller' ? [col, { name: 'Acciones', uid: 'receive_actions' }] : [col])
    : columnsWithTest

  const handleExportCustomRange = () => {
    setShowCustomDateModal(false)
    exportToCSV('custom_range', customStart, customEnd)
    setCustomStart('')
    setCustomEnd('')
  }

  return (
    <motion.div
      initial={{ y: 10, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: -10, opacity: 0 }}
      transition={{ duration: 0.3 }} className='space-y-4'
    >
      <div className='grid grid-cols-2'>
        <div className='my-4 flex items-center gap-3'>
          <Input
            label='Search'
            isClearable
            radius='lg'
            value={filterValue}
            onValueChange={onSearchChange}
            onClear={() => onClear()}
            classNames={{
              label: 'text-black/50 dark:text-white/90',
              input: [
                'bg-transparent',
                'text-black/90 dark:text-white/90',
                'placeholder:text-default-700/50 dark:placeholder:text-white/60'
              ],
              innerWrapper: 'bg-transparent',
              inputWrapper: [
                'bg-default-200/50',
                'dark:bg-default/60',
                'backdrop-blur-xl',
                'backdrop-saturate-200',
                'hover:bg-default-200/70',
                'dark:hover:bg-default/70',
                'group-data-[focus=true]:bg-default-200/50',
                'dark:group-data-[focus=true]:bg-default/60',
                '!cursor-text'
              ]
            }}
            placeholder='Buscar...'
            startContent={
              <MdOutlineSearch className='text-black/50 mb-0.5 dark:text-white/90 text-slate-400 pointer-events-none flex-shrink-0' />
        }
          />
          {isCoppelMexico && (isAdmin || isTiRegion) && (
            <>
              <Select
                label='Region'
                placeholder='Region'
                selectedKeys={selectedRegion ? [selectedRegion] : []}
                onChange={(e) => handleRegionChange(e.target.value)}
                isDisabled={isTiRegion}
                className='w-[300px]'
                variant='bordered'
                classNames={{ trigger: 'bg-white' }}
              >
                {regions.map((item: any) => {
                  const value = String(item.id ?? item.region ?? item.name ?? item)
                  const labelText = String(item.name ?? item.region ?? item)
                  return (
                    <SelectItem key={value} value={value} textValue={labelText}>
                      {labelText}
                    </SelectItem>
                  )
                })}
              </Select>
              <Select
                label='Tienda'
                placeholder='Tienda'
                selectedKeys={selectedStore ? [selectedStore] : []}
                onChange={(e) => setSelectedStore(e.target.value)}
                isDisabled={!selectedRegion}
                className='w-[500px]'
                variant='bordered'
                classNames={{ trigger: 'bg-white' }}
              >
                {stores.map((item: any) => {
                  const value = String(item.id ?? item.id)
                  const labelText = `${item.seller_store_number} - ${item.store}`
                  return (
                    <SelectItem key={value} value={value} textValue={labelText}>
                      {labelText}
                    </SelectItem>
                  )
                })}
              </Select>
            </>
          )}
        </div>
        <div className='flex items-center justify-end'>

          <Dropdown>
            <DropdownTrigger>
              <Button color='success' isLoading={loadingExcel} className='text-white' variant='shadow' endContent={<MdCloudDownload />}>Descargar</Button>
            </DropdownTrigger>
            <DropdownMenu aria-label='Static Actions'>
              <DropdownItem key='last_week' onPress={() => exportToCSV('last_week')}>Última semana</DropdownItem>
              <DropdownItem key='last_month' onPress={() => exportToCSV('last_month')}>Últimos 30 días</DropdownItem>
              <DropdownItem key='current_month' onPress={() => exportToCSV('current_month')}>Mes actual</DropdownItem>
              <DropdownItem key='previous_month' onPress={() => exportToCSV('previous_month')}>Mes anterior</DropdownItem>
              <DropdownItem key='all' onPress={() => exportToCSV('all')}>Todo</DropdownItem>
              <DropdownItem isDisabled={isHuaweiMexico || isWomChile || isHuaweiOnlineChile || isHuaweiOnlineMexico || isHuaweiOnlinePeru || isReuseChile || isConectaPeru || isRipleyPeru || isHuaweiPeru} key='custom' onPress={() => setShowCustomDateModal(true)}>Rango Personalizado</DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </div>
      </div>
      <Table
        aria-label='Example table with client side sorting'
        sortDescriptor={sortDescriptor}
        onSortChange={setSortDescriptor}
        bottomContent={bottomContent}
        bottomContentPlacement='outside'
      >
        <TableHeader columns={headerColumns}>
          {(column) => (
            <TableColumn key={column.uid} align={column.uid === 'actions' || column.uid === 'phonecheck_test' || column.uid === 'receive_actions' ? 'center' : 'start'} allowsSorting={column.uid !== 'actions' && column.uid !== 'phonecheck_test' && column.uid !== 'receive_actions'} className={column.uid === 'nombre' || column.uid === 'actions' || column.uid === 'ordenCompraCodigo' ? '' : 'md:table-cell'}>
              {column.name}
            </TableColumn>
          )}
        </TableHeader>
        <TableBody
          isLoading={loading}
          loadingContent={<Spinner label='Loading...' />}
        >
          {
            sortedItems.map((item: any) => (
              <TableRow key={item.id}>
                {(columnKey) => <TableCell>{renderCell(item, columnKey)}</TableCell>}
              </TableRow>
            ))
          }
        </TableBody>
      </Table>
      <CustomDateRangePicker
        open={showCustomDateModal}
        onClose={() => setShowCustomDateModal(false)}
        onExport={handleExportCustomRange}
        start={customStart}
        end={customEnd}
        setStart={setCustomStart}
        setEnd={setCustomEnd}
      />
    </motion.div>
  )
}

export default TablePrincipalLegacy
